// Minimal WebUSB driver for an FTDI FT232H used as a single-bit TTL trigger (D0 output)
// Replicates the small slice of FTDI's USB vendor protocol needed to drive one GPIO pin
// in asynchronous bitbang mode - the same mode pyftdi's GpioAsyncController uses.

import { restoreCursor, showCursor } from "./cursorVisibility";
import { showToastMessage } from "./toast";

const FTDI_VENDOR_ID = 0x0403;

const DISCONNECT_TOAST_MESSAGE =
  "Attention test administrator: Unable to communicate with sync pulse generator. Check USB cable.";
// Stays up until the reconnect handler dismisses it - the default 12s auto-dismiss would hide
// it long before an administrator mid-session notices and fixes the cable.
const DISCONNECT_TOAST_DURATION_MS = 24 * 60 * 60 * 1000;

const STATUS_BADGE_ID = "ttl-status-badge";
const CURSOR_REQUESTER_ID = "ttlBadge";

/**
 * Shows/hides a small fixed, clickable "TTL device not connected" indicator that re-triggers
 * the device picker on click (the same user-gesture-gated flow as the initial connect screen).
 * Automatically hidden on a successful connect/reconnect - callers only need to show it
 * (pass false) once they know a connection attempt has actually failed.
 */
export function setTTLStatusBadge(connected) {
  const existing = document.getElementById(STATUS_BADGE_ID);

  if (connected) {
    if (existing) existing.remove();
    restoreCursor(CURSOR_REQUESTER_ID);
    // Covers the manual click-to-reconnect path too, which never fires a 'connect' event
    // (that event is only for a previously-authorized device physically reappearing).
    if (dismissDisconnectToast) {
      dismissDisconnectToast();
      dismissDisconnectToast = null;
    }
    return;
  }
  if (existing) return;

  showCursor(CURSOR_REQUESTER_ID);

  const badge = document.createElement("button");
  badge.id = STATUS_BADGE_ID;
  badge.type = "button";
  badge.textContent = "⚠ TTL device not connected - click here to reconnect";
  badge.addEventListener("click", handleManualReconnectClick);
  badge.style.position = "fixed";
  badge.style.bottom = "12px";
  badge.style.left = "12px";
  badge.style.zIndex = "99999";
  badge.style.padding = "4px 10px";
  badge.style.borderRadius = "6px";
  badge.style.border = "none";
  badge.style.background = "rgba(0, 0, 0, 0.65)";
  badge.style.color = "#ff5c5c";
  badge.style.fontFamily = '"Motley Forces", sans-serif';
  badge.style.fontSize = "14px";
  badge.style.fontWeight = "bold";
  badge.style.letterSpacing = "0.2px";
  badge.style.cursor = "pointer";
  badge.dataset.clickableCursor = "true";
  document.body.appendChild(badge);
}

/**
 * Handles a click on the badge or disconnect toast - a real user gesture, so requestDevice() works.
 * Mid-game, the browser is typically in fullscreen; requestDevice() would force the browser to exit
 * fullscreen itself, which eats the click's user-activation and silently no-ops the device picker
 * (the same issue the initial connect screen dodges by running before enterFullscreenTrial - see
 * ttlDevice.js). Exiting fullscreen ourselves first, still inside this same click handler, keeps the
 * activation alive for requestDevice(). This fires a 'fullscreenchange' event, which the fullscreen
 * watchdog (see fullscreen.js) picks up on its own to prompt the experimenter back into fullscreen -
 * requestFullscreen() can't be restored automatically here since, by the time requestDevice()'s
 * device-chooser dialog and the async open/configure sequence resolve, the browser has consumed/
 * expired this click's transient activation (a hard browser security rule, not a timing bug).
 */
async function handleManualReconnectClick() {
  if (document.fullscreenElement) {
    try {
      await document.exitFullscreen();
    } catch (err) {
      console.error("[TTL] failed to exit fullscreen before reconnect:", err);
    }
  }

  try {
    await requestTTLDevice();
  } catch (err) {
    console.error("[TTL] manual reconnect failed:", err);
  }
}

// FTDI vendor request codes (SIO_REQ_*)
const SIO_RESET = 0x00;
const SIO_SET_BAUDRATE = 0x03;
const SIO_SET_LATENCY_TIMER = 0x09;
const SIO_SET_BITMODE = 0x0b;

const BITMODE_BITBANG = 0x01; // asynchronous bitbang mode

// D0 is the only pin we drive; the rest of the byte stays as inputs (direction mask bit0 = output)
const DIRECTION_MASK = 0x01;
const D0_HIGH = 0x01;
const D0_LOW = 0x00;

// FTDI's baudrate divisor fractional-part lookup table (index = div8 & 0x7)
const FRAC_DIV_CODE = [0, 3, 2, 4, 1, 5, 6, 7];

// FT232H is an "H series" chip: 12MHz reference clock, and its internal sample clock
// runs at baudrate*5 (instead of *16) while in bitbang mode
const HISPEED_CLOCK = 12000000;
const BITBANG_RATIO = 5;

// The bitbang "baudrate" only paces the chip's internal sample clock, not the pulse
// duration itself (that's timed in JS in pulseD0) - 9600 is a safe, well-supported value
const BITBANG_BAUDRATE = 9600;

let device = null;
let outEndpointNumber = null;
let dismissDisconnectToast = null;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Computes the wValue/wIndex pair for FTDI's SIO_SET_BAUDRATE request (H-series formula) */
function computeBaudrateDivisor(baudrate) {
  const effectiveBaudrate = Math.floor(baudrate / BITBANG_RATIO);
  const div8 = Math.round((8 * HISPEED_CLOCK) / effectiveBaudrate);
  let div = div8 >> 3;
  div |= FRAC_DIV_CODE[div8 & 0x7] << 14;
  div |= 0x00020000; // hispeed flag (H-series chips only)

  const value = div & 0xffff;
  const indexHigh = (div >> 16) & 0xffff;
  const index = (indexHigh << 8) | 0x01; // channel A, H-series index encoding
  return { value, index };
}

/** Sends a vendor OUT control request (no data stage) - index defaults to channel A */
async function vendorOut(request, value, index = 0x01) {
  await device.controlTransferOut({
    requestType: "vendor",
    recipient: "device",
    request,
    value,
    index,
  });
}

/** Whether a TTL device is currently open and configured for bitbang output */
export function isTTLDeviceConnected() {
  return !!device && outEndpointNumber !== null;
}

async function configureAsyncBitbang(selectedDevice) {
  const iface = selectedDevice.configuration.interfaces[0];
  const outEndpoint = iface.alternate.endpoints.find(
    (endpoint) => endpoint.direction === "out" && endpoint.type === "bulk"
  );
  if (!outEndpoint) throw new Error("No bulk OUT endpoint found on TTL device");

  device = selectedDevice;
  outEndpointNumber = outEndpoint.endpointNumber;

  await vendorOut(SIO_RESET, 0x00); // SIO_RESET_SIO
  await vendorOut(SIO_SET_LATENCY_TIMER, 1); // lowest latency timer (1ms)
  await vendorOut(SIO_SET_BITMODE, (DIRECTION_MASK & 0xff) | (BITMODE_BITBANG << 8));

  const { value, index } = computeBaudrateDivisor(BITBANG_BAUDRATE);
  await vendorOut(SIO_SET_BAUDRATE, value, index);

  // Force D0 low right away so the pin has a known, driven state at init instead of
  // sitting at whatever level it floated to (or was left at) before bitbang mode was set.
  // Without this, a floating-high pin can make a high->low->high pulse invisible on a scope.
  await device.transferOut(outEndpointNumber, new Uint8Array([D0_LOW]));

  setTTLStatusBadge(true);
}

/** Opens, claims, and configures a picked/previously-authorized FTDI device for D0 bitbang output */
async function openAndConfigure(usbDevice) {
  await usbDevice.open();
  if (usbDevice.configuration === null) await usbDevice.selectConfiguration(1);
  await usbDevice.claimInterface(0);

  await configureAsyncBitbang(usbDevice);
}

/**
 * Prompts the browser's USB device picker (must be called from a user gesture, e.g. a click
 * handler) and configures the selected FTDI board for D0 async bitbang output.
 */
export async function requestTTLDevice() {
  if (!navigator.usb) throw new Error("WebUSB is not supported in this browser");

  const selected = await navigator.usb.requestDevice({
    filters: [{ vendorId: FTDI_VENDOR_ID }],
  });

  await openAndConfigure(selected);
  return true;
}

/**
 * Silently reconnects to a previously-authorized FTDI device, if any - no user gesture required.
 * Meant to be tried once per session before falling back to requestTTLDevice().
 * @returns {Promise<boolean>} Whether a device was found and configured
 */
export async function reconnectTTLDevice() {
  if (!navigator.usb) return false;

  const devices = await navigator.usb.getDevices();
  const match = devices.find((candidate) => candidate.vendorId === FTDI_VENDOR_ID);
  if (!match) return false;

  await openAndConfigure(match);
  return true;
}

// The browser only ever fires connect/disconnect for devices this origin has already been
// granted permission to access (i.e. ones requestTTLDevice() picked in an earlier session), so
// these listeners can't hijack an unrelated FTDI board and don't need a pulseTTLtrialStart
// check - if we were never authorized, they simply never fire.
if (navigator.usb) {
  // WebUSB invalidates the open USBDevice on unplug: transferOut/controlTransferOut start
  // rejecting instead of silently going nowhere. Drop our reference immediately so
  // isTTLDeviceConnected() reflects reality and pulseTTL() no-ops instead of throwing.
  navigator.usb.addEventListener("disconnect", (event) => {
    if (event.device !== device) return;

    console.warn("[TTL] device disconnected");
    device = null;
    outEndpointNumber = null;

    setTTLStatusBadge(false);
    dismissDisconnectToast = showToastMessage(DISCONNECT_TOAST_MESSAGE, {
      durationMs: DISCONNECT_TOAST_DURATION_MS,
      onClick: handleManualReconnectClick,
    });
  });

  // Re-plugging the same board re-fires 'connect' for it. Reconfigure right away so the
  // experimenter doesn't have to interrupt the game to revisit the connect screen.
  navigator.usb.addEventListener("connect", async (event) => {
    if (event.device.vendorId !== FTDI_VENDOR_ID) return;

    console.log("[TTL] device reconnected, reconfiguring...");
    try {
      await openAndConfigure(event.device);
      showToastMessage("Sync pulse generator reconnected.");
    } catch (err) {
      console.error("[TTL] auto-reconnect failed:", err);
    }
  });
}

/**
 * Sets D0 high and resolves as soon as that transfer completes - the earliest point a caller
 * can pair a timestamp with the rising edge actually going out. Setting D0 low again after
 * `ms` milliseconds happens in the background; callers should not await that tail inside a
 * trial's on_load.
 */
export async function pulseD0(ms = 100) {
  if (!isTTLDeviceConnected()) throw new Error("TTL device not connected");

  await device.transferOut(outEndpointNumber, new Uint8Array([D0_HIGH]));

  (async () => {
    await sleep(ms);
    try {
      await device.transferOut(outEndpointNumber, new Uint8Array([D0_LOW]));
    } catch (err) {
      console.error("[TTL] failed to release D0 low:", err);
    }
  })();
}
